import React, {
  CSSProperties,
  useEffect,
  useMemo,
  useState,
  KeyboardEvent,
  useRef,
} from 'react';
import {
  VirtualizedTableCellProps,
  VirtualizedTableColumnFit,
  VirtualizedTableColumnProps,
  VirtualizedTableKey,
  VirtualizedTableRowAction,
  VirtualizedTableRowProps,
  VirtualizedTableSelectionType,
} from './VirtualizedTable.interface';
import { VirtualizedTableActionsColumn } from './VirtualizedTableRowActionsCell';
import { VirtualizedTableRowColumnsCell } from './VirtualizedTableRowColumnsCell';
import { Checkbox } from '@fluentui/react-components';
import { Button } from '../Button/Button';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import classNames from 'classnames';
import { calculateGridTemplateColumns } from './VirtualizedTable.utils';

export interface VirtualizedTableRowPops {
  actions: VirtualizedTableRowAction[] | undefined;
  columns: VirtualizedTableColumnProps[];
  editMode: boolean;
  columnFit: VirtualizedTableColumnFit;
  hasRowIcons: boolean;
  className: string | undefined;
  onDoubleClick?: () => void;
  onEditCancelButtonClick?: (rowKey: VirtualizedTableKey) => void;
  onEditSaveButtonClick?: (
    rowKey: VirtualizedTableKey,
    editedCells: VirtualizedTableCellProps[],
  ) => void;
  row: VirtualizedTableRowProps;
  selectedRowKeys: VirtualizedTableKey[];
  selectionType: VirtualizedTableSelectionType;
  setSelectedRowKeys: (newSelectedKeys: VirtualizedTableKey[]) => void;
  style: CSSProperties;
  viewport: {
    width: number | undefined;
    height: number | undefined;
  };
  onMultiSelectClick?: (cellIndex: number) => void;
  rowIndex: number;
  onFocus?: (rowKey: VirtualizedTableKey) => void;
  focused: boolean;
}

export const VirtualizedTableRow: React.FC<VirtualizedTableRowPops> = ({
  actions,
  className,
  columns,
  editMode,
  columnFit,
  hasRowIcons,
  onDoubleClick,
  onEditCancelButtonClick,
  onEditSaveButtonClick,
  row,
  selectedRowKeys,
  selectionType,
  setSelectedRowKeys,
  style,
  viewport,
  onMultiSelectClick,
  rowIndex,
  onFocus,
  focused,
}) => {
  const { t } = useTranslation();

  const rowReference = useRef<HTMLDivElement | null>(null);

  const [rowEditCellValues, setRowEditCellValues] = useState<
    VirtualizedTableCellProps[] | null
  >(null);

  useEffect(() => {
    if (editMode) {
      setRowEditCellValues([...row.cells]);
    } else {
      setRowEditCellValues(null);
    }
  }, [editMode, row.cells]);

  const handleEditCellChange = (cellIndex: number, newValue: string) => {
    if (rowEditCellValues === null) {
      return;
    }

    const copy = cloneDeep(rowEditCellValues);
    copy[cellIndex].value = newValue;

    setRowEditCellValues(copy);
  };

  const handleOnEditSaveButtonClick = () => {
    if (onEditSaveButtonClick && rowEditCellValues) {
      onEditSaveButtonClick(row.key, rowEditCellValues);
    }
  };

  const gridTemplateColumns: string = useMemo(() => {
    return calculateGridTemplateColumns(
      selectionType !== VirtualizedTableSelectionType.None,
      hasRowIcons,
      columnFit === VirtualizedTableColumnFit.Fit,
      actions !== undefined,
      columns,
    );
  }, [actions, columnFit, columns, hasRowIcons, selectionType]);

  const handleOnCheckboxChange = (checked: boolean) => {
    if (selectionType === VirtualizedTableSelectionType.Single) {
      setSelectedRowKeys(checked ? [row.key] : []);
    } else {
      setSelectedRowKeys(
        checked
          ? [...selectedRowKeys, row.key]
          : selectedRowKeys.filter(item => item !== row.key),
      );
    }
  };

  useEffect(() => {
    focused && rowReference.current?.focus();
  }, [focused]);

  return (
    <>
      <div
        ref={rowReference}
        role="row"
        aria-rowindex={rowIndex + 1}
        style={{
          ...style,
          display: 'grid',
          gridTemplateColumns: gridTemplateColumns,
        }}
        className={classNames(className, 'row')}
        key={row.key}
        tabIndex={0}
        onDoubleClick={onDoubleClick}
        onFocus={_event => {
          onFocus && onFocus(row.key);
        }}
        aria-label={
          onDoubleClick
            ? t('table_editable_column', { defaultValue: 'Editierbare und auswählbare Zeile, die Zeile kann durch Eingabe bearbeitet und durch drücken der Leertaste ausgewählt werden' })
            : undefined
        }
        onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
          switch (event.code) {
            case 'Space':
              event.preventDefault();
              event.stopPropagation();
              handleOnCheckboxChange(!selectedRowKeys.includes(row.key));
              break;
            case 'Enter':
              if (onDoubleClick) {
                event.preventDefault();
                event.stopPropagation();
                onDoubleClick();
              }
              break;
          }
        }}
      >
        {/** Make sure your table rows are the same height as what you passed into the list... */}

        {hasRowIcons && (
          <div role="cell" className="iconCol cell">
            {row.icon}
          </div>
        )}
        {selectionType !== VirtualizedTableSelectionType.None && (
          <div role="cell" className="checkboxCol cell">
            <Checkbox
              checked={selectedRowKeys.includes(row.key)}
              disabled={row.isSelectable === false}
              onChange={(_event, data) => handleOnCheckboxChange(data.checked === true)}
              input={{ tabIndex: -1 }}
              aria-label={t('table_select_row', { defaultValue: 'Wählt die Zeile durch Drücken der Leertaste aus' })}
            />
          </div>
        )}
        {
          <VirtualizedTableRowColumnsCell
            row={row}
            columnFit={columnFit}
            columns={columns}
            editMode={editMode && rowEditCellValues !== null}
            onEditCellChange={handleEditCellChange}
            rowEditCellValues={rowEditCellValues}
            onMultiSelectClick={(cellIndex: number) => {
              if (!onMultiSelectClick) {
                return;
              }

              onMultiSelectClick(cellIndex);
            }}
          />
        }
        {actions && (
          <VirtualizedTableActionsColumn actions={actions} row={row} />
        )}
        {editMode && (
          <div role="cell" className="rowEditActions" key="buttons">
            <div className="cell">
              <div
                className="row-edit-action-buttons"
                style={{
                  // One button has a width of 112 px, blus half margin to next button,
                  // so we need to center the buttons
                  left: viewport.width ? viewport.width / 2 - 117 : 0,
                }}
              >
                <Button
                  key="cancel-button"
                  onClick={() => onEditCancelButtonClick?.(row.key)}
                  onKeyDown={(event: KeyboardEvent) => {
                    if (event.code === 'Enter' || event.code === 'Space') {
                      onEditCancelButtonClick?.(row.key);
                    }
                  }}
                  aria-label={t('table_cancel_edit', { defaultValue: 'Bricht das Bearbeiten der Zeile durch Drücken der Leertaste oder der Eingabetaste ab' })}
                >
                  {t('cancel', { defaultValue: 'Abbrechen' })}
                </Button>
                <Button
                  appearance="primary"
                  key="save-button"
                  onClick={handleOnEditSaveButtonClick}
                  onKeyDown={(event: KeyboardEvent) => {
                    if (event.code === 'Enter' || event.code === 'Space') {
                      onEditCancelButtonClick?.(row.key);
                    }
                  }}
                  aria-label={t('table_save_edit', { defaultValue: 'Speichert das Bearbeiten der Zeile durch Drücken der Leertaste oder der Eingabetaste ab' })}
                >
                  {t('save', { defaultValue: 'Speichern' })}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
